import { DateTime } from 'luxon'

import { ICellValue, ITableRow } from 'types'

import { CalendarEvent, EventSegment, PillColour, WeekRow } from './types'

export const TRACK_CAPACITY = 4

// Week-row geometry: each event track is TRACK_HEIGHT tall, below the day number and above the
// "N more…" label. A standard 140px row holds exactly TRACK_CAPACITY tracks.
export const TRACK_HEIGHT = 22
const DAY_NUMBER_HEIGHT = 32
const OVERFLOW_LABEL_HEIGHT = 20

/** How many event tracks fit in a week row of the given height (fit-to-height rows share the insert). */
export const getTrackCapacityForRowHeight = (rowHeight: number): number =>
  Math.max(0, Math.floor((rowHeight - DAY_NUMBER_HEIGHT - OVERFLOW_LABEL_HEIGHT) / TRACK_HEIGHT))

/**
 * Compute the 6-week grid of visible dates for the given month, Monday-first.
 * Always returns exactly 42 days (6 rows x 7 cols). Leading days come from the
 * previous month; trailing days from the next.
 */
export function getVisibleWeeks(currentMonth: DateTime): WeekRow[] {
  const firstOfMonth = currentMonth.startOf('month')
  const gridStart = firstOfMonth.startOf('week') // Luxon .startOf('week') is Monday by default
  const rows: WeekRow[] = []
  for (let weekIndex = 0; weekIndex < 6; weekIndex++) {
    const weekStart = gridStart.plus({ weeks: weekIndex })
    const days: DateTime[] = []
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      days.push(weekStart.plus({ days: dayIndex }))
    }
    rows.push({
      weekStart,
      days,
      segments: [],
      overflowByDay: [0, 0, 0, 0, 0, 0, 0]
    })
  }
  return rows
}

/**
 * Signature used to read a cell value for a given row + columnPublicId. The
 * calendar helper does not want to know about `columnIdToIndex` / the compressed
 * column store, so the caller builds this closure once and passes it in.
 *
 * A typical implementation looks like:
 *   (row, columnId) => getRowValue(row, columnId, columnIdToIndex, columnStore)
 */
export type GetCellValue = (row: ITableRow, columnPublicId: string) => ICellValue | undefined

/**
 * Per-row callback that returns the pill colour for a row, or null if no
 * row-level colour rule (i.e. an `applyToEntireRow` rule) matched it.
 */
export type GetRowColour = (row: ITableRow) => PillColour | null

/**
 * Normalise raw rows into CalendarEvent objects.
 *
 * - Rows without a valid start date are filtered out.
 * - If end < start, end is normalised to start (treated as single-day bad data).
 * - datetime values are preserved with their time component on the returned
 *   event, so renderers can format a `timeLabel` for single-day datetime events.
 *   Month-view placement still uses the date portion only.
 * - When ``getRowColour`` is provided and returns a colour for the row, it is
 *   attached to the event so the renderer can paint the pill with the row's
 *   row-level colour-rule colours.
 */
export function rowsToEvents(
  rows: ITableRow[],
  labelColumnPublicId: string,
  startColumnPublicId: string,
  endColumnPublicId: string | null,
  getCellValue: GetCellValue,
  getRowColour?: GetRowColour
): CalendarEvent[] {
  const events: CalendarEvent[] = []
  for (const row of rows) {
    const rawStart = getCellValue(row, startColumnPublicId)
    const start = parseDateValue(rawStart)
    if (!start) continue

    let end: DateTime | null = null
    if (endColumnPublicId) {
      const rawEnd = getCellValue(row, endColumnPublicId)
      const parsed = parseDateValue(rawEnd)
      if (parsed) {
        end = parsed < start ? start : parsed
      }
    }

    const rawLabel = getCellValue(row, labelColumnPublicId)
    const label = formatLabelValue(rawLabel)

    events.push({
      id: row.publicId,
      label,
      start,
      end,
      rowId: row.publicId,
      colour: getRowColour ? getRowColour(row) : null
    })
  }
  return events
}

/**
 * Parse a cell value from a date/datetime column into a Luxon DateTime.
 * Accepts ISO strings (the standard wire format for date/datetime columns)
 * and falls back to null for anything unrecognised.
 */
function parseDateValue(value: ICellValue | undefined): DateTime | null {
  if (value == null || typeof value !== 'string' || value === '') return null
  const parsed = DateTime.fromISO(value)
  return parsed.isValid ? parsed : null
}

/**
 * Convert any column value into a pill label string. Catches the common cases:
 * strings, numbers, null, objects with a 'name' or 'label' field (for select options).
 */
function formatLabelValue(value: ICellValue | undefined): string {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) {
    // multiselect / multilink / attachment etc — join string-like entries
    const parts: string[] = []
    for (const entry of value) {
      if (typeof entry === 'string') {
        parts.push(entry)
      } else if (entry && typeof entry === 'object') {
        const obj = entry as unknown as Record<string, unknown>
        if (typeof obj.name === 'string') parts.push(obj.name)
        else if (typeof obj.label === 'string') parts.push(obj.label)
      }
    }
    return parts.join(', ')
  }
  if (typeof value === 'object') {
    const obj = value as unknown as Record<string, unknown>
    if (typeof obj.name === 'string') return obj.name
    if (typeof obj.label === 'string') return obj.label
  }
  return ''
}

/**
 * Return the earliest event start DateTime across the given events, or null if empty.
 * Used by the container to decide the initial month on first load.
 */
export function earliestEventStart(events: CalendarEvent[]): DateTime | null {
  let earliest: DateTime | null = null
  for (const e of events) {
    if (!earliest || e.start < earliest) earliest = e.start
  }
  return earliest
}

/**
 * Lay out events onto the provided week rows.
 *
 * Mutates each WeekRow to populate .segments (with track assignments) and
 * .overflowByDay.
 *
 * Algorithm (per the spec, §6):
 *   1. For each week, clip overlapping events into segments.
 *   2. Sort segments: startDay asc, span desc, label asc (case-insensitive), eventId asc.
 *   3. Greedy track assignment — lowest free track wins.
 *   4. Segments with track >= trackCapacity are dropped and increment overflowByDay.
 */
export function layoutMultiDayEvents(
  events: CalendarEvent[],
  visibleWeeks: WeekRow[],
  trackCapacity: number = TRACK_CAPACITY
): void {
  for (const week of visibleWeeks) {
    const weekStart = week.weekStart
    const weekEnd = weekStart.plus({ days: 6 })

    // Step 1: clip events into segments
    const rawSegments: EventSegment[] = []
    for (const event of events) {
      const eventStart = event.start.startOf('day')
      const eventEnd = (event.end ?? event.start).startOf('day')
      if (eventEnd < weekStart || eventStart > weekEnd) continue

      const clippedStart = eventStart < weekStart ? weekStart : eventStart
      const clippedEnd = eventEnd > weekEnd ? weekEnd : eventEnd

      // Math.round (not floor) absorbs DST-day fractional diffs (23h or 25h spans
      // return 0.958 / 1.042 days respectively); Math.floor would drop an event
      // onto the wrong column on DST-transition weekends twice a year.
      const startDay = Math.round(clippedStart.diff(weekStart, 'days').days)
      const endDay = Math.round(clippedEnd.diff(weekStart, 'days').days)

      const isMultiDay = !eventStart.equals(eventEnd)
      const timeLabel =
        !isMultiDay && event.start.hour + event.start.minute + event.start.second !== 0
          ? event.start.toFormat('h:mm a')
          : null

      rawSegments.push({
        eventId: event.id,
        label: event.label,
        startDay,
        endDay,
        isContinuationFromPrev: eventStart < weekStart,
        isContinuedNext: eventEnd > weekEnd,
        isMultiDay,
        track: -1, // placeholder, assigned below
        timeLabel,
        colour: event.colour
      })
    }

    // Step 2: sort
    rawSegments.sort((a, b) => {
      if (a.startDay !== b.startDay) return a.startDay - b.startDay
      const aSpan = a.endDay - a.startDay
      const bSpan = b.endDay - b.startDay
      if (aSpan !== bSpan) return bSpan - aSpan
      const aLabel = (a.label ?? '').toLowerCase()
      const bLabel = (b.label ?? '').toLowerCase()
      if (aLabel !== bLabel) return aLabel.localeCompare(bLabel)
      return a.eventId.localeCompare(b.eventId)
    })

    // Step 3 + 4: greedy track assignment + overflow cut
    const trackOccupancy: Array<Set<number>> = []
    const assignedSegments: EventSegment[] = []
    const overflow = [0, 0, 0, 0, 0, 0, 0]

    for (const seg of rawSegments) {
      // Find lowest free track
      let assignedTrack = -1
      for (let t = 0; t < trackOccupancy.length; t++) {
        let conflict = false
        for (let d = seg.startDay; d <= seg.endDay; d++) {
          if (trackOccupancy[t].has(d)) {
            conflict = true
            break
          }
        }
        if (!conflict) {
          assignedTrack = t
          break
        }
      }
      if (assignedTrack === -1) {
        assignedTrack = trackOccupancy.length
        trackOccupancy.push(new Set<number>())
      }
      for (let d = seg.startDay; d <= seg.endDay; d++) {
        trackOccupancy[assignedTrack].add(d)
      }

      if (assignedTrack < trackCapacity) {
        assignedSegments.push({ ...seg, track: assignedTrack })
      } else {
        for (let d = seg.startDay; d <= seg.endDay; d++) {
          overflow[d] += 1
        }
      }
    }

    // Mutate the week row
    week.segments = assignedSegments
    week.overflowByDay = overflow
  }
}
